import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listOrders, PortalOrder } from '../../services/portalApi';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { StatusBadge } from './PortalShared';

const STATUSES = ['draft', 'submitted', 'in_progress', 'pending_assistance', 'error', 'completed', 'cancelled'];

const PortalOrdersPage: React.FC = () => {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const { customer, logout } = usePortalAuth();
  const { t } = useTranslation();
  const [orders, setOrders] = useState<PortalOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const LIMIT = 20;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await listOrders({ status: statusFilter || undefined, limit: LIMIT, offset });
      setOrders(data.orders);
      setTotal(data.total_count);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [statusFilter, offset]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
            {customer?.org_logo_url ? (
              <img src={customer.org_logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm leading-none">{customer?.org_name ?? customer?.name ?? orgAlias}</p>
            <p className="text-xs text-gray-400 leading-none mt-0.5">{t('portal.orders.partsPortal')}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">{customer?.contact_name || customer?.name}</span>
          <Link
            to={`/portal/${orgAlias}/orders/new`}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {t('portal.orders.newOrder')}
          </Link>
          <Link
            to={`/portal/${orgAlias}/chat`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            💬 {t('portal.chat.title', 'AI Chat')}
          </Link>
          <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
            {t('portal.orders.signOut')}
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('portal.orders.title')}</h1>
          <span className="text-sm text-gray-500">{total} {total !== 1 ? t('portal.orders.orders') : t('portal.orders.order')}</span>
        </div>

        {/* Filter bar */}
        <div className="flex gap-2 mb-5 flex-wrap">
          <button
            onClick={() => { setStatusFilter(''); setOffset(0); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!statusFilter ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}
          >
            {t('portal.orders.filterAll')}
          </button>
          {STATUSES.map((s) => (
            <button key={s}
              onClick={() => { setStatusFilter(s); setOffset(0); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}
            >
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">{t('portal.orders.loading')}</div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="font-medium text-gray-500">{t('portal.orders.noOrders')}</p>
              <p className="text-sm mt-1">
                <Link to={`/portal/${orgAlias}/orders/new`} className="text-blue-600 hover:underline">{t('portal.orders.placeFirst')}</Link>
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {[t('portal.orders.colOrder'), t('portal.orders.colVin'), t('portal.orders.colService'), t('portal.orders.colParts'), t('portal.orders.colStatus'), t('portal.orders.colDate'), ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map((order) => {
                  const itemCount = order.parts_in_order?.items?.length ?? 0;
                  return (
                    <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{order.external_order_id}</p>
                        <p className="text-xs text-gray-400 font-mono">{order.transaction_id?.slice(0, 8)}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-700">{order.vin || '—'}</td>
                      <td className="px-4 py-3 capitalize text-gray-600">{order.service_type?.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-gray-600">{itemCount} {itemCount !== 1 ? t('portal.orders.items') : t('portal.orders.item')}</td>
                      <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                      <td className="px-4 py-3 text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/portal/${orgAlias}/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium text-xs">
                          {t('portal.orders.view')}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">{t('portal.orders.showing', { from: offset + 1, to: Math.min(offset + LIMIT, total), total })}</p>
            <div className="flex gap-2">
              <button onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                {t('portal.orders.previous')}
              </button>
              <button onClick={() => setOffset(offset + LIMIT)} disabled={offset + LIMIT >= total}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                {t('portal.orders.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalOrdersPage;
